import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS as F } from "@shared/constants/fields";
import { boardStep3 } from "@shared/interview/board-ch3";
import type { CardSlot } from "./PersonPicker";
import { TileRowEngine, type EngineBinding } from "./engine/TileRowEngine";

/**
 * s237 (chapter three PR 2; plan W1–W18) — WORK, chapter three's first screen, as CONFIGURATION of the shared tile-to-row
 * engine (D-236.61). Everything the board draws comes from `Chapter3.dc.html` `work`; this file says only where each
 * answer is stored (the board never does):
 *   • A job / Other work → a Career row (personal-information-career), kind Job / Other work (D-237.27);
 *   • My own business / Self-employed → a Business row (financial-business-self-employment), kind Own business /
 *     Self-employed (D-237.27); an empty kind reads as Job / Own business.
 *   • The employer is ONE company card (W6, D-235.72; rule 150): its name names a linked company (payer_name keeps the
 *     words), its main phone and email, the address the letter goes to — open even when empty (D-237.21) — and the person
 *     to ask for, the HR contact (D-236.50-6).
 *   • Who takes over: a person card (W15). Who else owns it: up to three people (W16, D-235.73), "not asked" on a
 *     self-employed row (the board's rule).
 *   • The row note shows for Who takes over only (D-237.28).
 *   • None of these is saved for Career in client_section_na (D-237.26) — Business is not manual-N/A eligible (it is
 *     gated by owns_business), so its rows are hidden and nothing else is written for it.
 */

const STEP = boardStep3("work");

const company: CardSlot[] = [
  { label: "The company", name: { kind: "text", key: F.EMPLOYER }, phone: F.EMPLOYER_PHONE, email: F.EMPLOYER_EMAIL, address: F.EMPLOYER_ADDRESS, org: true },
  { label: "A person to ask for", name: { kind: "contact", key: F.HR_CONTACT_NAME }, phone: F.HR_CONTACT_PHONE, email: F.HR_CONTACT_EMAIL, address: F.HR_CONTACT_ADDRESS },
];
const takesOver: CardSlot[] = [
  { label: "Person 1", name: { kind: "text", key: F.SUCCESSION_CONTACT }, phone: F.SUCCESSION_CONTACT_PHONE, email: F.SUCCESSION_CONTACT_EMAIL, address: F.SUCCESSION_CONTACT_ADDRESS },
];
const coOwners: CardSlot[] = [
  { label: "Person 1", name: { kind: "contact", key: F.JOINT_OWNER_NAME }, phone: F.JOINT_OWNER_PHONE, email: F.JOINT_OWNER_EMAIL, address: F.JOINT_OWNER_ADDRESS },
  { label: "Person 2", name: { kind: "contact", key: F.CO_OWNER_2 }, phone: F.CO_OWNER_2_PHONE, email: F.CO_OWNER_2_EMAIL, address: F.CO_OWNER_2_ADDRESS },
  { label: "Person 3", name: { kind: "contact", key: F.CO_OWNER_3 }, phone: F.CO_OWNER_3_PHONE, email: F.CO_OWNER_3_EMAIL, address: F.CO_OWNER_3_ADDRESS },
];

export const WORK_BINDING: EngineBinding = {
  screen: "work",
  sources: [
    {
      categorySlug: CATEGORY_SLUGS.PERSONAL_INFORMATION,
      sectionSlug: SECTION_SLUGS.PERSONAL_INFORMATION_CAREER,
      kindField: F.CAREER_KIND,
      kindOf: { job: "Job", role: "Other work" },
      defaultTile: "job",
    },
    {
      categorySlug: CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE,
      sectionSlug: SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT,
      kindField: F.BUSINESS_KIND,
      kindOf: { biz: "Own business", self: "Self-employed" },
      defaultTile: "biz",
      leadField: { biz: F.BIZ_NAME, self: F.BIZ_NAME },
    },
  ],
  cells: {
    call: { kind: "card", slots: company, moreLabel: "+ Add a person to ask for", openAddressWhenEmpty: true },
    empno: { kind: "field", key: F.EMPLOYEE_NUMBER },
    kind: { kind: "field", key: F.BIZ_TYPE },
    role: { kind: "field", key: F.ROLE },
    worth: { kind: "field", key: F.VALUE },
    takes: { kind: "card", slots: takesOver },
    co: { kind: "card", slots: coOwners, moreLabel: "+ Add another person" },
  },
  noneSections: [SECTION_SLUGS.PERSONAL_INFORMATION_CAREER],
  rowNote: { cell: "takes", noteKey: "own" },
};

export function WorkScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return <TileRowEngine step={STEP} binding={WORK_BINDING} clientId={clientId} onAnswered={onAnswered} />;
}
